import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import UploadFileInput from '@repo/ui/file-input'
import { ImageIcon, UploadIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import { TranslationLocale } from '../../../../lib/types/enums/translation/translation.enum'
import { OptionInput } from '../../../../lib/types/models/options/option-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../../lib/utils/validations/file.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { createTranslationsSchema } from '../../../../lib/utils/validations/translations-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import TranslationsInput from '../../../elements/inputs/translations-input'
import ValidationButtons from '../../../elements/validation-buttons'

const formSchema: ZodType<OptionInput> = z.object({
  code: createStringSchema('Code', { nullish: true }),
  translations: createTranslationsSchema([TranslationLocale.FR]),
  picto: FILE_VALIDATION().nullish(),
  picture: FILE_VALIDATION().nullish()
})

const JourneyPlaceCreateOptionPage = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<OptionInput>) => {
  const form = useForm<OptionInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox title="Option">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Code" />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="h-4" />

          <TranslationsInput
            control={form.control}
            name="translations"
            label="NOM"
            requiredLocales={[TranslationLocale.FR]}
          />
          <div className="h-4" />
          <FormField
            control={form.control}
            name={'picto'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <UploadFileInput
                    maxSize={3 * 1024 * 1024} // 3MB
                    allowedAspectRatio="1:1"
                    allowedExtensions={['png']}
                    placeholder="Glisser-déposer ou parcourir"
                    placeholderVariant="tag"
                    placeholderColor={tailwindColors.grey.lighter}
                    types={[FileTypeEnum.IMAGE]}
                    icon={UploadIcon}
                    label={'Télécharger un picto'}
                    description={
                      <div className="flex flex-col justify-center items-center gap-1">
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Téléchargez votre icône uniquement au format&nbsp;
                          <span className="font-medium text-grey-light-active">PNG</span>
                        </Typography>
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Dimensions recommandées :&nbsp;
                          <span className="font-medium text-grey-light-active">
                            307x307 pixels
                          </span>
                        </Typography>
                        <Typography variant="small" color={tailwindColors.grey.light.active}>
                          Maintenez un rapport d'aspect de&nbsp;
                          <span className="font-medium text-grey-light-active">1:1</span>
                        </Typography>
                      </div>
                    }
                    files={field?.value ? [field?.value] : undefined}
                    onFilesChange={(files) => {
                      field.onChange(files.length === 0 ? null : files[0])
                    }}
                    onFileError={(error) => {
                      if (error) toast.error(error)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="h-4" />
          <FormField
            control={form.control}
            name={'picture'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <UploadFileInput
                    placeholder="Glisser-déposer ou parcourir"
                    types={[FileTypeEnum.IMAGE]}
                    icon={ImageIcon}
                    label={'Déposer des images'}
                    files={field?.value ? [field?.value] : undefined}
                    onFilesChange={(files) => {
                      field.onChange(files.length === 0 ? null : files[0])
                    }}
                    onFileError={(error) => {
                      if (error) toast.error(error)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>
      </form>
      <ValidationButtons {...validationButtonProps} />
    </Form>
  )
}

export default JourneyPlaceCreateOptionPage
